import { BRAND, NAV_LINKS } from "@/lib/brand";
import { Linkedin, Twitter, Github } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleAnchor = (href) => (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-[rgba(244,234,222,0.10)] bg-[#0B0A0C]">
      <div className="container-narrow py-12 sm:py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Link to="/" className="inline-flex items-center gap-3">
              <img src={BRAND.logoUrl} alt={BRAND.name} className="h-14 w-auto" />
              <div className="flex flex-col leading-none">
                <span className="font-display text-lg font-bold tracking-tight text-[#F4EADE]">
                  GREINCH<span className="text-coral">.</span> WW LLC
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6] mt-1">
                  International B2B · Development & Brokerage
                </span>
              </div>
            </Link>
            <p className="mt-5 text-sm text-[#EFE3D6]/75 leading-relaxed max-w-md">
              You ask, we develop. We build the systems and broker the partnerships that
              move ambitious B2B operators across borders.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">Navigate</div>
            <ul className="mt-4 space-y-2">
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    onClick={handleAnchor(l.href)}
                    data-testid={`footer-${l.testid}`}
                    className="text-sm text-[#EFE3D6]/80 hover:text-coral transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">Legal</div>
            <ul className="mt-4 space-y-2">
              <li><Link to="/privacy" data-testid="footer-privacy-link" className="text-sm text-[#EFE3D6]/80 hover:text-coral transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" data-testid="footer-terms-link" className="text-sm text-[#EFE3D6]/80 hover:text-coral transition-colors">Terms of Service</Link></li>
              <li><Link to="/cookies" data-testid="footer-cookies-link" className="text-sm text-[#EFE3D6]/80 hover:text-coral transition-colors">Cookies Policy</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">Reach out</div>
            <ul className="mt-4 space-y-2 text-sm text-[#EFE3D6]/80">
              <li>
                <a href={`mailto:${BRAND.email}`} className="hover:text-coral transition-colors" data-testid="footer-email-link">
                  {BRAND.email}
                </a>
              </li>
              <li>{BRAND.address}</li>
              <li>{BRAND.addressLine2}</li>
              <li className="text-[#B9AFA6]">Worldwide coverage · EMEA, AMER, APAC</li>
            </ul>
            <div className="mt-5 flex items-center gap-3">
              <a href={BRAND.socials.linkedin} aria-label="LinkedIn" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[rgba(244,234,222,0.14)] hover:border-[#E94B4B]/50 hover:text-[#E94B4B] transition-colors text-[#EFE3D6]" data-testid="footer-linkedin-link">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href={BRAND.socials.twitter} aria-label="Twitter" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[rgba(244,234,222,0.14)] hover:border-[#E94B4B]/50 hover:text-[#E94B4B] transition-colors text-[#EFE3D6]" data-testid="footer-twitter-link">
                <Twitter className="h-4 w-4" />
              </a>
              <a href={BRAND.socials.github} aria-label="GitHub" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[rgba(244,234,222,0.14)] hover:border-[#E94B4B]/50 hover:text-[#E94B4B] transition-colors text-[#EFE3D6]" data-testid="footer-github-link">
                <Github className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-[rgba(244,234,222,0.08)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#B9AFA6]">
            © {new Date().getFullYear()} GREINCH WW LLC · All rights reserved
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#B9AFA6]">
            You ask <span className="text-coral">·</span> We develop
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
